// ./src/components/features/Connectors/ConfigEditors/GoogleSheetsConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Text,
  Anchor,
  Alert,
  Code,
  Switch,
  Group,
  Box,
  List,
  SegmentedControl,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconTable,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { GoogleSheetsConnectorConfig } from "@/types";

export interface GoogleSheetsConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// FormSubmits shared service account email - users share their sheets with this
const FORMSUBMITS_SERVICE_ACCOUNT_EMAIL =
  "sheets@formsubmits.iam.gserviceaccount.com";

// Default empty configuration
const DEFAULT_CONFIG: GoogleSheetsConnectorConfig = {
  credentials: "",
  spreadsheetId: "",
  sheetName: "Sheet1",
  includeMetadata: true,
  includeHeaders: true,
};

/**
 * Extract Spreadsheet ID from a Google Sheets URL or return the input if it's already an ID
 * Supports formats:
 * - https://docs.google.com/spreadsheets/d/SPREADSHEET_ID/edit
 * - https://docs.google.com/spreadsheets/d/SPREADSHEET_ID/edit#gid=0
 * - https://docs.google.com/spreadsheets/d/SPREADSHEET_ID
 * - Just the SPREADSHEET_ID
 */
const extractSpreadsheetId = (input: string): string => {
  const trimmed = input.trim();
  if (!trimmed) return "";

  // Try to match Google Sheets URL pattern
  const urlPattern =
    /(?:https?:\/\/)?(?:docs\.google\.com\/spreadsheets\/d\/)([a-zA-Z0-9_-]+)/;
  const match = trimmed.match(urlPattern);

  if (match && match[1]) {
    return match[1];
  }

  // If no URL pattern matched, assume it's already an ID
  // Valid spreadsheet IDs contain only alphanumeric characters, hyphens, and underscores
  if (/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
    return trimmed;
  }

  // Return as-is if it doesn't match expected patterns (will show validation error)
  return trimmed;
};

/**
 * Parse and validate Google Sheets connector configuration
 */
const parseGoogleSheetsConfig = (
  jsonString: string
): GoogleSheetsConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const GoogleSheetsConnectorConfigEditor: React.FC<
  GoogleSheetsConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<GoogleSheetsConnectorConfig>(() =>
    parseGoogleSheetsConfig(value)
  );

  // Determine auth mode based on whether credentials are provided
  const [authMode, setAuthMode] = useState<"shared" | "custom">(() => {
    const parsed = parseGoogleSheetsConfig(value);
    return parsed.credentials && parsed.credentials.trim() !== ""
      ? "custom"
      : "shared";
  });

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseGoogleSheetsConfig(value);
    setConfig(parsed);
    setAuthMode(
      parsed.credentials && parsed.credentials.trim() !== ""
        ? "custom"
        : "shared"
    );
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: GoogleSheetsConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle auth mode change
  const handleAuthModeChange = (newMode: string) => {
    const mode = newMode as "shared" | "custom";
    setAuthMode(mode);
    if (mode === "shared") {
      // Clear credentials when switching to shared mode
      updateConfig({ ...config, credentials: "" });
    }
  };

  // Handle field changes
  const handleCredentialsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    updateConfig({ ...config, credentials: e.target.value });
  };

  const handleSpreadsheetIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const extractedId = extractSpreadsheetId(e.target.value);
    updateConfig({ ...config, spreadsheetId: extractedId });
  };

  const handleSheetNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, sheetName: e.target.value });
  };

  const handleIncludeMetadataChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, includeMetadata: e.currentTarget.checked });
  };

  const handleIncludeHeadersChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, includeHeaders: e.currentTarget.checked });
  };

  // Validate credentials JSON (only for custom mode)
  const credentialsError = (() => {
    if (authMode !== "custom" || !config.credentials) return undefined;
    try {
      const parsed = JSON.parse(config.credentials);
      if (!parsed.client_email || !parsed.private_key) {
        return "Missing required fields (client_email, private_key)";
      }
    } catch {
      return "Invalid JSON format";
    }
    return undefined;
  })();

  // Validate spreadsheet ID format
  const spreadsheetIdError = (() => {
    if (!config.spreadsheetId) return undefined;
    if (!/^[a-zA-Z0-9_-]+$/.test(config.spreadsheetId)) {
      return "Invalid Spreadsheet ID format";
    }
    return undefined;
  })();

  // Extract service account email for display (custom mode)
  const customServiceAccountEmail = (() => {
    if (authMode !== "custom" || !config.credentials) return null;
    try {
      const parsed = JSON.parse(config.credentials);
      return parsed.client_email || null;
    } catch {
      return null;
    }
  })();

  return (
    <Stack gap="md">
      {/* Auth Mode Selection */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Authentication Method
        </Text>
        <SegmentedControl
          value={authMode}
          onChange={handleAuthModeChange}
          disabled={disabled}
          fullWidth
          data={[
            { label: "Easy Setup (Recommended)", value: "shared" },
            { label: "Use Own Service Account", value: "custom" },
          ]}
        />
      </Box>

      {/* Shared Mode Instructions */}
      {authMode === "shared" && (
        <>
          <Alert icon={<IconTable size={16} />} color="green" variant="light">
            <Text size="sm" fw={500} mb="xs">
              Easy Setup (2 steps):
            </Text>
            <List size="sm" spacing={4}>
              <List.Item>
                Share your Google Sheet with the email below (Editor access)
              </List.Item>
              <List.Item>
                Paste the Google Sheets URL or Spreadsheet ID
              </List.Item>
            </List>
          </Alert>

          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            <Text size="sm" fw={500} mb="xs">
              Share your sheet with this email (Editor access):
            </Text>
            <Code block>{FORMSUBMITS_SERVICE_ACCOUNT_EMAIL}</Code>
          </Alert>
        </>
      )}

      {/* Custom Mode Instructions */}
      {authMode === "custom" && (
        <>
          <Alert icon={<IconTable size={16} />} color="gray" variant="light">
            <Text size="sm" fw={500} mb="xs">
              Custom Service Account Setup:
            </Text>
            <List size="sm" spacing={4}>
              <List.Item>
                Create a Service Account in{" "}
                <Anchor
                  href="https://console.cloud.google.com/iam-admin/serviceaccounts"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Cloud Console
                </Anchor>
              </List.Item>
              <List.Item>
                Enable the <strong>Google Sheets API</strong> in your project
              </List.Item>
              <List.Item>Create and download the JSON key file</List.Item>
              <List.Item>
                Share your Google Sheet with the service account email (Editor
                access)
              </List.Item>
            </List>
          </Alert>

          {/* Credentials Field */}
          <Textarea
            label="Service Account Credentials (JSON)"
            placeholder='{"type": "service_account", "project_id": "...", ...}'
            description="Paste the entire JSON key file contents"
            value={config.credentials || ""}
            onChange={handleCredentialsChange}
            disabled={disabled}
            required
            minRows={4}
            maxRows={8}
            autosize
            styles={{ input: { fontFamily: "monospace", fontSize: "12px" } }}
            error={credentialsError}
          />

          {/* Show service account email if valid */}
          {customServiceAccountEmail && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              color="yellow"
              variant="light"
            >
              <Text size="sm">
                <strong>Important:</strong> Share your Google Sheet with this
                email (Editor access):
              </Text>
              <Code block mt="xs">
                {customServiceAccountEmail}
              </Code>
            </Alert>
          )}
        </>
      )}

      {/* Spreadsheet ID Field */}
      <TextInput
        label="Google Sheet URL or Spreadsheet ID"
        placeholder="https://docs.google.com/spreadsheets/d/1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms/edit"
        description="Paste the full URL or just the Spreadsheet ID — we'll extract what we need"
        value={config.spreadsheetId}
        onChange={handleSpreadsheetIdChange}
        disabled={disabled}
        required
        size="md"
        error={spreadsheetIdError}
      />

      {/* Sheet Name Field */}
      <TextInput
        label="Sheet/Tab Name"
        placeholder="Sheet1"
        description="Name of the sheet tab (default: Sheet1)"
        value={config.sheetName || "Sheet1"}
        onChange={handleSheetNameChange}
        disabled={disabled}
        size="md"
      />

      {/* Toggle Options */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Options
        </Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Text size="sm">Include Metadata</Text>
              <Text size="xs" c="dimmed">
                Add Timestamp and Submission ID columns
              </Text>
            </div>
            <Switch
              checked={config.includeMetadata !== false}
              onChange={handleIncludeMetadataChange}
              disabled={disabled}
            />
          </Group>

          <Group justify="space-between">
            <div>
              <Text size="sm">Auto-Create Headers</Text>
              <Text size="xs" c="dimmed">
                Add header row if sheet is empty
              </Text>
            </div>
            <Switch
              checked={config.includeHeaders !== false}
              onChange={handleIncludeHeadersChange}
              disabled={disabled}
            />
          </Group>
        </Stack>
      </Box>

      {/* How It Works */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>Each form submission is appended as a new row</List.Item>
          <List.Item>
            Columns are created from form field labels (in form field order)
          </List.Item>
          <List.Item>
            Hidden fields and security fields are automatically excluded
          </List.Item>
          <List.Item>
            Headers are auto-created on first submission if sheet is empty
          </List.Item>
        </List>
      </Alert>

      {/* Warning about field changes */}
      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="orange"
        variant="light"
      >
        <Text size="sm" fw={500} mb="xs">
          Important: Don't change form fields after collecting data
        </Text>
        <Text size="sm">
          Once you start collecting submissions, avoid adding, removing, or
          reordering form fields. Changes will cause new rows to be misaligned
          with existing headers. If you need to change fields, create a new
          sheet or manually update the headers.
        </Text>
      </Alert>
    </Stack>
  );
};
